'use client';

import './print-styles.css';

import {
  ArrowRight,
  Brain,
  Building2,
  ChartLine,
  ClipboardCheck,
  Code,
  Eye,
  FileText,
  GitBranch,
  Globe,
  Hash,
  Heart,
  Kanban,
  Mail,
  MessageCircle,
  Phone,
  Plane,
  Puzzle,
  Rocket,
  SearchCheck,
  Settings,
  Share2,
  ShieldCheck,
  Users,
  Webhook,
  Workflow,
  Zap,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';

export default function CheatsheetPage() {
  const { t, locale } = useTranslation();
  const isAr = locale === 'ar';

  return (
    <div className="bg-background min-h-screen">
      {/* Print Header - Only visible when printing. Demoted to h2 so we
          don't ship two h1 elements on the same page (Semrush flagged this). */}
      <div className="hidden print:mb-4 print:block">
        <div className="border-b-4 border-black pb-3">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-serif text-2xl">
                {t.cheatsheet['Open Platform Cheatsheet']}
              </h2>
              <p className="mt-1 text-xs text-neutral-600">
                {
                  t.cheatsheet[
                    'Complete Quick Reference Guide | open.cx | Updated January 2026'
                  ]
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="border-border from-background via-background to-muted/30 border-b bg-gradient-to-br print:border-none print:bg-white">
        <div className="container pt-32 pb-16 print:py-0">
          <div className="mx-auto max-w-5xl">
            {/* Web view */}
            <div className="text-center print:hidden">
              <h1 className="mb-3 font-serif text-4xl md:text-5xl">
                {t.cheatsheet['Open Platform Cheatsheet']}
              </h1>
              <p className="text-muted-foreground mx-auto mb-6 max-w-2xl text-base">
                {
                  t.cheatsheet[
                    'Quick reference guide to all Open features, integrations, and capabilities'
                  ]
                }
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <Button asChild size="sm" className="gap-2">
                  <Link
                    href="/demo"
                    aria-label={
                      t.common['Learn more about Open and book a demo']
                    }
                  >
                    {t.footer['Get Started']}{' '}
                    <ArrowRight className="size-3.5 rtl:rotate-180" />
                  </Link>
                </Button>
                <Button
                  asChild
                  size="sm"
                  variant="outline"
                  onClick={() => window.print()}
                >
                  <button>{t.cheatsheet['Save as PDF']}</button>
                </Button>
              </div>
            </div>

            {/* Print view - Company overview */}
            <div className="hidden print:block">
              <div className="mb-4 grid grid-cols-2 gap-4">
                {/* Left column - About */}
                <div>
                  <h2 className="mb-2 text-base">
                    {t.cheatsheet['About Open']}
                  </h2>
                  <p className="mb-3 text-xs leading-relaxed text-neutral-700">
                    {
                      t.cheatsheet[
                        'Open is the complete AI customer support platform trusted by leading enterprises worldwide. We automate 60-80% of support interactions across phone, email, chat, WhatsApp, and more with 95%+ accuracy.'
                      ]
                    }
                  </p>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex items-center gap-2">
                      <div className="size-1.5 rounded-full bg-green-600"></div>
                      <span className="font-semibold">
                        {t.cheatsheet['77% avg automation rate']}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="size-1.5 rounded-full bg-blue-600"></div>
                      <span className="font-semibold">
                        {t.cheatsheet['95%+ AI accuracy']}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="size-1.5 rounded-full bg-purple-600"></div>
                      <span className="font-semibold">
                        {t.cheatsheet['100+ languages supported']}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="size-1.5 rounded-full bg-orange-600"></div>
                      <span className="font-semibold">
                        {t.cheatsheet['99.9% uptime SLA']}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Right column - Backed by & Top Customers */}
                <div>
                  <h3 className="mb-2 text-xs text-neutral-600 uppercase">
                    {t.cheatsheet['Backed By']}
                  </h3>
                  <div className="mb-3 flex flex-wrap items-center gap-3">
                    <img
                      src="/investors/yc.png"
                      alt="Y Combinator"
                      className="h-6 object-contain opacity-70"
                    />
                    <img
                      src="/investors/shorooq.png"
                      alt="Shorooq"
                      className="h-5 object-contain opacity-70"
                    />
                    <img
                      src="/investors/spacetime.png"
                      alt="Spacetime"
                      className="h-5 object-contain opacity-70"
                    />
                  </div>

                  <h3 className="mb-2 text-xs text-neutral-600 uppercase">
                    {t.cheatsheet['Trusted By']}
                  </h3>
                  <div className="text-xs leading-relaxed text-neutral-700">
                    {
                      t.cheatsheet[
                        'MoneyGram (55M customers), Mollie (€6.5B), TicketSwap (15M users), FareHarbor (Booking.com), Trunkrs, Qoyod, and more'
                      ]
                    }
                  </div>
                </div>
              </div>

              {/* Quick stats bar */}
              <div className="mb-4 grid grid-cols-4 gap-2 border-y border-neutral-300 py-2">
                <div className="text-center">
                  <div className="text-lg font-bold">6+</div>
                  <div className="text-xs text-neutral-600">
                    {t.cheatsheet['Channels']}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold">50+</div>
                  <div className="text-xs text-neutral-600">
                    {t.cheatsheet['Integrations']}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold">7+</div>
                  <div className="text-xs text-neutral-600">
                    {t.cheatsheet['Helpdesks']}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold">55M+</div>
                  <div className="text-xs text-neutral-600">
                    {t.cheatsheet['End Users']}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Cheatsheet Grid */}
      <section className="container pt-8 pb-5">
        <div className="grid gap-5 lg:grid-cols-2">
          {/* AI CHANNELS */}
          <div className="border-border bg-card rounded-lg border">
            <div className="border-b border-blue-200 bg-blue-50 px-5 py-3 dark:border-blue-900 dark:bg-blue-950/30">
              <h2 className="text-base text-blue-900 dark:text-blue-100">
                {t.cheatsheet['AI Communication Channels']}
              </h2>
              <p className="text-xs text-blue-700 dark:text-blue-300">
                {t.cheatsheet['Deploy AI agents across all touchpoints']}
              </p>
            </div>
            <div className="divide-border divide-y p-4">
              <Link
                href="/channels/voice"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <Phone className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.nav['AI Voice']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Natural phone conversations']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/channels/chat"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <MessageCircle className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.nav['AI Web (chat)']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Intelligent chat widget']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/channels/email"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <Mail className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.nav['AI Email']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Automated email support']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/channels/whatsapp"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <MessageCircle className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.nav['AI WhatsApp']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['WhatsApp Business API']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/channels/social"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <Share2 className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.nav['AI Social']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Unified social support']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/channels/slack"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <Hash className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.nav['AI Slack']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Internal & external Slack']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>
            </div>
          </div>

          {/* AI ENGINE */}
          <div className="border-border bg-card rounded-lg border">
            <div className="border-b border-purple-200 bg-purple-50 px-5 py-3 dark:border-purple-900 dark:bg-purple-950/30">
              <h2 className="text-base text-purple-900 dark:text-purple-100">
                {t.cheatsheet['The AI Engine (Agent 5)']}
              </h2>
              <p className="text-xs text-purple-700 dark:text-purple-300">
                {t.cheatsheet['Core AI capabilities powering all agents']}
              </p>
            </div>
            <div className="divide-border divide-y p-4">
              <Link
                href="/agent5#training"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <Brain className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['Training Center']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Documents, FAQs, knowledge bases']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/agent5#observability"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <Eye className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['Observability']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Real-time monitoring & analytics']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/agent5#actions"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <Zap className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['Actions & Integrations']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Connect to any API or system']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/agent5#compliance"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <ShieldCheck className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['Compliance & Security']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['GDPR, SOC 2, PII redaction']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/agent5#evals"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <ClipboardCheck className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['Evaluations (Evals)']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Test AI quality & accuracy']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>
            </div>
          </div>

          {/* AGENTIC WORKFLOWS */}
          <div className="border-border bg-card rounded-lg border">
            <div className="border-b border-green-200 bg-green-50 px-5 py-3 dark:border-green-900 dark:bg-green-950/30">
              <h2 className="text-base text-green-900 dark:text-green-100">
                {t.cheatsheet['Agentic Workflows']}
              </h2>
              <p className="text-xs text-green-700 dark:text-green-300">
                {t.cheatsheet['Automate complex multi-step processes']}
              </p>
            </div>
            <div className="divide-border divide-y p-4">
              <Link
                href="/workflows"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <Workflow className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['Workflow Builder']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Visual editor for automation']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/outbound-sequencing"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <GitBranch className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['Outbound Sequencing']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Proactive customer outreach']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/customer-insights"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <ChartLine className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['Customer Insights']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Extract actionable insights']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/knowledge-gap-detection"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <SearchCheck className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['Knowledge Gap Detection']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Identify missing KB info']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/templates"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <Puzzle className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['Templates']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['50+ pre-built workflows']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>
            </div>
          </div>

          {/* HELPDESK INTEGRATIONS */}
          <div className="border-border bg-card rounded-lg border">
            <div className="border-b border-orange-200 bg-orange-50 px-5 py-3 dark:border-orange-900 dark:bg-orange-950/30">
              <h2 className="text-base text-orange-900 dark:text-orange-100">
                {t.cheatsheet['Helpdesk Integrations']}
              </h2>
              <p className="text-xs text-orange-700 dark:text-orange-300">
                {t.cheatsheet['Native ticketing system integrations']}
              </p>
            </div>
            <div className="divide-border divide-y p-4">
              <Link
                href="/helpdesks/open"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="flex size-8 items-center justify-center rounded-md border bg-white p-1.5 dark:bg-neutral-900">
                    <img
                      src="/open-logo.svg"
                      alt="Open"
                      className="size-full object-contain"
                    />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['Open Helpdesk']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['AI-native']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/helpdesks/zendesk"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="flex size-8 items-center justify-center rounded-md border bg-white p-1.5 dark:bg-neutral-900">
                    <img
                      src="/helpdicks/zendesk.png"
                      alt="Zendesk"
                      className="size-full object-contain"
                    />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">Zendesk</div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Ticket management']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/helpdesks/intercom"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="flex size-8 items-center justify-center rounded-md border bg-white p-1.5 dark:bg-neutral-900">
                    <img
                      src="/helpdicks/intercom.png"
                      alt="Intercom"
                      className="size-full object-contain"
                    />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">Intercom</div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Inbox routing']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/helpdesks/salesforce"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="flex size-8 items-center justify-center rounded-md border bg-white p-1.5 dark:bg-neutral-900">
                    <img
                      src="/helpdicks/salesforce.png"
                      alt="Salesforce"
                      className="size-full object-contain"
                    />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">Salesforce</div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Service Cloud']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/helpdesks/freshdesk"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="flex size-8 items-center justify-center rounded-md border bg-white p-1.5 dark:bg-neutral-900">
                    <img
                      src="/helpdicks/freshdesk.png"
                      alt="Freshdesk"
                      className="size-full object-contain"
                    />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">Freshdesk</div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['SLA management']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/helpdesks/hubspot"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="flex size-8 items-center justify-center rounded-md border bg-white p-1.5 dark:bg-neutral-900">
                    <img
                      src="/helpdicks/hubspot.png"
                      alt="HubSpot"
                      className="size-full object-contain"
                    />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">HubSpot</div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Service Hub']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/helpdesks/twilio-flex"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="flex size-8 items-center justify-center rounded-md border bg-white p-1.5 dark:bg-neutral-900">
                    <img
                      src="/helpdicks/flex.png"
                      alt="Twilio Flex"
                      className="size-full object-contain"
                    />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">Twilio Flex</div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Contact center']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>
            </div>
          </div>

          {/* AI TRAINING SOURCES */}
          <div className="border-border bg-card rounded-lg border lg:col-span-2">
            <div className="border-b border-cyan-200 bg-cyan-50 px-5 py-3 dark:border-cyan-900 dark:bg-cyan-950/30">
              <h2 className="text-base text-cyan-900 dark:text-cyan-100">
                {t.cheatsheet['AI Training Sources']}
              </h2>
              <p className="text-xs text-cyan-700 dark:text-cyan-300">
                {
                  t.cheatsheet[
                    'Connect knowledge bases and data sources to train your AI'
                  ]
                }
              </p>
            </div>
            <div className="grid gap-0 sm:grid-cols-2 md:grid-cols-3">
              {/* Column 1 */}
              <div className="divide-border divide-y p-4 md:border-e">
                <div className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-2.5 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="text-muted-foreground flex size-6 items-center justify-center">
                      <Globe className="size-4" />
                    </div>
                    <div className="text-sm">{t.cheatsheet['Web Crawler']}</div>
                  </div>
                </div>
                <Link
                  href="/integrations/notion"
                  className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-2.5 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex size-6 items-center justify-center">
                      <img
                        src="https://cdn.simpleicons.org/notion/000000"
                        alt="Notion"
                        className="size-4 object-contain dark:invert"
                      />
                    </div>
                    <div className="text-sm">Notion</div>
                  </div>
                </Link>
                <Link
                  href="/integrations/confluence"
                  className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-2.5 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex size-6 items-center justify-center">
                      <img
                        src="https://cdn.simpleicons.org/confluence/172B4D"
                        alt="Confluence"
                        className="size-4 object-contain"
                      />
                    </div>
                    <div className="text-sm">Confluence</div>
                  </div>
                </Link>
                <Link
                  href="/integrations/google-docs"
                  className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-2.5 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex size-6 items-center justify-center">
                      <img
                        src="https://cdn.simpleicons.org/googledocs/4285F4"
                        alt="Google Docs"
                        className="size-4 object-contain"
                      />
                    </div>
                    <div className="text-sm">Google Docs</div>
                  </div>
                </Link>
                <div className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-2.5 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="flex size-6 items-center justify-center">
                      <img
                        src="https://cdn.simpleicons.org/gitbook/3884FF"
                        alt="Gitbook"
                        className="size-4 object-contain"
                      />
                    </div>
                    <div className="text-sm">Gitbook</div>
                  </div>
                </div>
                <Link
                  href="/integrations/github"
                  className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-2.5 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex size-6 items-center justify-center">
                      <img
                        src="https://cdn.simpleicons.org/github/181717"
                        alt="GitHub"
                        className="size-4 object-contain dark:invert"
                      />
                    </div>
                    <div className="text-sm">GitHub</div>
                  </div>
                </Link>
                <Link
                  href="/integrations/slack"
                  className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-2.5 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex size-6 items-center justify-center">
                      <img
                        src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRK8A-gJxrkrg04BpHlXQPpXZfmYWQO04vopA&s"
                        alt="Slack"
                        className="size-4 object-contain"
                      />
                    </div>
                    <div className="text-sm">Slack</div>
                  </div>
                </Link>
                <Link
                  href="/integrations/dropbox"
                  className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-2.5 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex size-6 items-center justify-center">
                      <img
                        src="https://cdn.simpleicons.org/dropbox/0061FF"
                        alt="Dropbox"
                        className="size-4 object-contain"
                      />
                    </div>
                    <div className="text-sm">Dropbox</div>
                  </div>
                </Link>
                <Link
                  href="/integrations/onedrive"
                  className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-2.5 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex size-6 items-center justify-center">
                      <img
                        src="https://play-lh.googleusercontent.com/pkzkr91OWFffdDGZ9706Ev2lxjM1pMizefY__r8JkCAtNVO-hmaMG2Qfx9ngpu7V7K4Yx_E7csAMl6fP7dGNS28=w240-h480-rw"
                        alt="OneDrive"
                        className="size-4 object-contain"
                      />
                    </div>
                    <div className="text-sm">OneDrive</div>
                  </div>
                </Link>
                <Link
                  href="/integrations/sharepoint"
                  className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-2.5 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex size-6 items-center justify-center">
                      <img
                        src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRWwTXuUhrLQNxdSkETztUhZbqJkedqLeftkQ&s"
                        alt="SharePoint"
                        className="size-4 object-contain"
                      />
                    </div>
                    <div className="text-sm">SharePoint</div>
                  </div>
                </Link>
              </div>

              {/* Column 2 */}
              <div className="divide-border divide-y p-4 md:border-e">
                <Link
                  href="/integrations/zendesk-help-center"
                  className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-2.5 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex size-6 items-center justify-center">
                      <img
                        src="https://cdn.simpleicons.org/zendesk/03363D"
                        alt="Zendesk"
                        className="size-4 object-contain"
                      />
                    </div>
                    <div className="text-sm">
                      {t.cheatsheet['Zendesk Help Center']}
                    </div>
                  </div>
                </Link>
                <Link
                  href="/integrations/intercom-articles"
                  className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-2.5 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex size-6 items-center justify-center">
                      <img
                        src="https://cdn.simpleicons.org/intercom/6AFDEF"
                        alt="Intercom"
                        className="size-4 object-contain"
                      />
                    </div>
                    <div className="text-sm">
                      {t.cheatsheet['Intercom Articles']}
                    </div>
                  </div>
                </Link>
                <Link
                  href="/integrations/google-sheets"
                  className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-2.5 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex size-6 items-center justify-center">
                      <img
                        src="https://cdn.simpleicons.org/googlesheets/34A853"
                        alt="Google Sheets"
                        className="size-4 object-contain"
                      />
                    </div>
                    <div className="text-sm">Google Sheets</div>
                  </div>
                </Link>
                <Link
                  href="/integrations/airtable"
                  className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-2.5 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex size-6 items-center justify-center">
                      <img
                        src="https://cdn.simpleicons.org/airtable/18BFFF"
                        alt="Airtable"
                        className="size-4 object-contain"
                      />
                    </div>
                    <div className="text-sm">Airtable</div>
                  </div>
                </Link>
                <Link
                  href="/integrations/coda"
                  className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-2.5 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex size-6 items-center justify-center">
                      <img
                        src="https://cdn.simpleicons.org/coda/F46A54"
                        alt="Coda"
                        className="size-4 object-contain"
                      />
                    </div>
                    <div className="text-sm">Coda</div>
                  </div>
                </Link>
                <Link
                  href="/integrations/asana"
                  className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-2.5 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex size-6 items-center justify-center">
                      <img
                        src="https://cdn.simpleicons.org/asana/F06A6A"
                        alt="Asana"
                        className="size-4 object-contain"
                      />
                    </div>
                    <div className="text-sm">Asana</div>
                  </div>
                </Link>
                <Link
                  href="/integrations/monday"
                  className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-2.5 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex size-6 items-center justify-center">
                      {/* Simple Icons dropped the Monday mark and its CDN
                          slug now 404s; generic board icon until a licensed
                          asset lands in /public. */}
                      <Kanban className="size-4" />
                    </div>
                    <div className="text-sm">Monday.com</div>
                  </div>
                </Link>
                <Link
                  href="/integrations/hubspot-kb"
                  className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-2.5 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex size-6 items-center justify-center">
                      <img
                        src="https://cdn.simpleicons.org/hubspot/FF7A59"
                        alt="HubSpot"
                        className="size-4 object-contain"
                      />
                    </div>
                    <div className="text-sm">{t.cheatsheet['HubSpot KB']}</div>
                  </div>
                </Link>
              </div>

              {/* Column 3 */}
              <div className="divide-border divide-y p-4">
                <Link
                  href="/integrations/mongodb"
                  className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-2.5 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex size-6 items-center justify-center">
                      <img
                        src="https://cdn.simpleicons.org/mongodb/47A248"
                        alt="MongoDB"
                        className="size-4 object-contain"
                      />
                    </div>
                    <div className="text-sm">MongoDB</div>
                  </div>
                </Link>
                <Link
                  href="/integrations/snowflake"
                  className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-2.5 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex size-6 items-center justify-center">
                      <img
                        src="https://cdn.simpleicons.org/snowflake/29B5E8"
                        alt="Snowflake"
                        className="size-4 object-contain"
                      />
                    </div>
                    <div className="text-sm">Snowflake</div>
                  </div>
                </Link>
                <Link
                  href="/integrations/bigquery"
                  className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-2.5 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex size-6 items-center justify-center">
                      <img
                        src="https://cdn.simpleicons.org/googlebigquery/669DF6"
                        alt="BigQuery"
                        className="size-4 object-contain"
                      />
                    </div>
                    <div className="text-sm">BigQuery</div>
                  </div>
                </Link>
                <div className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-2.5 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="flex size-6 items-center justify-center">
                      <img
                        src="https://cdn.simpleicons.org/shopify/7AB55C"
                        alt="Shopify"
                        className="size-4 object-contain"
                      />
                    </div>
                    <div className="text-sm">Shopify</div>
                  </div>
                </div>
                <div className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-2.5 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="flex size-6 items-center justify-center">
                      <img
                        src="https://cdn.simpleicons.org/jira/0052CC"
                        alt="Jira"
                        className="size-4 object-contain"
                      />
                    </div>
                    <div className="text-sm">Jira</div>
                  </div>
                </div>
                <div className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-2.5 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="flex size-6 items-center justify-center">
                      <img
                        src="https://cdn.simpleicons.org/zapier/FF4A00"
                        alt="Zapier"
                        className="size-4 object-contain"
                      />
                    </div>
                    <div className="text-sm">Zapier</div>
                  </div>
                </div>
                <div className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-2.5 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="text-muted-foreground flex size-6 items-center justify-center">
                      <Zap className="size-4" />
                    </div>
                    <div className="text-sm">Webhooks</div>
                  </div>
                </div>
                <div className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-2.5 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="text-muted-foreground flex size-6 items-center justify-center">
                      <Code className="size-4" />
                    </div>
                    <div className="text-sm">REST API</div>
                  </div>
                </div>
                <Link
                  href="/integrations"
                  className="hover:bg-muted/50 group bg-primary/5 mt-4 flex items-center justify-between rounded-md border px-3 py-2.5 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="text-primary flex size-6 items-center justify-center">
                      <ArrowRight className="size-4 rtl:rotate-180" />
                    </div>
                    <div className="text-primary text-sm font-semibold">
                      {isAr ? 'كل التكاملات' : 'View All Integrations'}
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </div>

          {/* INDUSTRIES */}
          <div className="border-border bg-card rounded-lg border">
            <div className="border-b border-indigo-200 bg-indigo-50 px-5 py-3 dark:border-indigo-900 dark:bg-indigo-950/30">
              <h2 className="text-base text-indigo-900 dark:text-indigo-100">
                {t.cheatsheet['Industry Solutions']}
              </h2>
              <p className="text-xs text-indigo-700 dark:text-indigo-300">
                {t.cheatsheet['Purpose-built for specific industries']}
              </p>
            </div>
            <div className="divide-border divide-y p-4">
              <Link
                href="/industries/financial-services"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <Building2 className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['Financial Services']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Compliant AI for banking']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/industries/healthcare"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <Heart className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['Healthcare']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['HIPAA-compliant patient support']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/industries/mobility"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <Plane className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['Mobility & Transportation']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Ride-sharing & logistics']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/industries/technology"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <Zap className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['Technology & SaaS']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Reduce churn with AI']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/industries/travel-hospitality"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <Building2 className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['Travel & Hospitality']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Bookings & guest services']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>
            </div>
          </div>

          {/* OUTBOUND */}
          <div className="border-border bg-card rounded-lg border">
            <div className="border-b border-rose-200 bg-rose-50 px-5 py-3 dark:border-rose-900 dark:bg-rose-950/30">
              <h2 className="text-base text-rose-900 dark:text-rose-100">
                {t.cheatsheet['Outbound Sequencing']}
              </h2>
              <p className="text-xs text-rose-700 dark:text-rose-300">
                {t.cheatsheet['Proactive outreach with AI across channels']}
              </p>
            </div>
            <div className="divide-border divide-y p-4">
              <Link
                href="/outbound-sequencing"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <Phone className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['Phone (AI Voice)']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Automated outbound calls']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/outbound-sequencing"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <Mail className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['Email (AI)']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Automated email campaigns']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/outbound-sequencing"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <MessageCircle className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['SMS (AI)']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Text message sequences']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/outbound-sequencing"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <MessageCircle className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['WhatsApp (AI)']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['WhatsApp outbound messaging']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/outbound-sequencing"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <Globe className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['Web (AI Chat)']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Proactive chat outreach']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>
            </div>
          </div>

          {/* FREE TOOLS */}
          <div className="border-border bg-card rounded-lg border">
            <div className="border-b border-pink-200 bg-pink-50 px-5 py-3 dark:border-pink-900 dark:bg-pink-950/30">
              <h2 className="text-base text-pink-900 dark:text-pink-100">
                {t.cheatsheet['Free Tools & Resources']}
              </h2>
              <p className="text-xs text-pink-700 dark:text-pink-300">
                {t.cheatsheet['Calculators and assessment tools']}
              </p>
            </div>
            <div className="divide-border divide-y p-4">
              <Link
                href="/tools/roi-calculator"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <ChartLine className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['ROI Calculator']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Calculate cost savings']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/tools/ai-readiness"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <ClipboardCheck className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['AI Readiness Assessment']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Evaluate AI readiness']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/tools/team-size-calculator"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <Users className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['Team Size Calculator']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Optimize team structure']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/tools/support-budget"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <FileText className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['Budget Planner']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Plan support operations budget']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/tools/benchmark"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <ChartLine className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['Benchmark Tool']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Compare to industry standards']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/open-academy"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <Rocket className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['Open Academy']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Learn AI support best practices']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ANALYTICS & INSIGHTS */}
      <section className="container pt-0 pb-8">
        <div className="grid gap-5 lg:grid-cols-2">
          <div className="border-border bg-card rounded-lg border">
            <div className="border-b border-violet-200 bg-violet-50 px-5 py-3 dark:border-violet-900 dark:bg-violet-950/30">
              <h2 className="text-base text-violet-900 dark:text-violet-100">
                {t.cheatsheet['Analytics & Insights']}
              </h2>
              <p className="text-xs text-violet-700 dark:text-violet-300">
                {t.cheatsheet['Extract actionable insights from conversations']}
              </p>
            </div>
            <div className="divide-border divide-y p-4">
              <Link
                href="/customer-insights"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <ChartLine className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['Customer Insights']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Voice of customer analysis']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/knowledge-gap-detection"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <SearchCheck className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['Knowledge Gap Detection']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Identify missing documentation']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <div className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-3">
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <Brain className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['Smart Clustering']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Group similar issues']}
                    </div>
                  </div>
                </div>
              </div>

              <div className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-3">
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <Eye className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['Root Cause Analysis']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Identify actual causes']}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* SECURITY & COMPLIANCE */}
          <div className="border-border bg-card rounded-lg border">
            <div className="border-b border-red-200 bg-red-50 px-5 py-3 dark:border-red-900 dark:bg-red-950/30">
              <h2 className="text-base text-red-900 dark:text-red-100">
                {t.cheatsheet['Security & Compliance']}
              </h2>
              <p className="text-xs text-red-700 dark:text-red-300">
                {t.cheatsheet['Enterprise-grade security standards']}
              </p>
            </div>
            <div className="divide-border divide-y p-4">
              <Link
                href="/gdpr"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <ShieldCheck className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['GDPR Compliant']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['EU data residency']}
                    </div>
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <div className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-3">
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <ShieldCheck className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['SOC 2 Type II']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['In progress']}
                    </div>
                  </div>
                </div>
              </div>

              <div className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-3">
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <ShieldCheck className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['PII Redaction']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Automatic data protection']}
                    </div>
                  </div>
                </div>
              </div>

              <div className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-3">
                <div className="flex items-center gap-3">
                  <div className="text-muted-foreground flex size-8 items-center justify-center">
                    <FileText className="size-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">
                      {t.cheatsheet['Audit Logs']}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.cheatsheet['Full activity tracking']}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* API & DEVELOPER */}
      <section className="container pt-5 pb-8">
        <div className="border-border bg-card rounded-lg border">
          <div className="border-b border-slate-200 bg-slate-50 px-5 py-3 dark:border-slate-900 dark:bg-slate-950/30">
            <h2 className="text-base text-slate-900 dark:text-slate-100">
              {t.cheatsheet['API & Developer Resources']}
            </h2>
            <p className="text-xs text-slate-700 dark:text-slate-300">
              {t.cheatsheet['Build custom integrations and automations']}
            </p>
          </div>
          <div className="divide-border divide-y p-4">
            <a
              href="https://docs.open.cx/api-reference"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="text-muted-foreground flex size-8 items-center justify-center">
                  <Code className="size-5" />
                </div>
                <div>
                  <div className="text-sm font-semibold">
                    {t.cheatsheet['REST API']}
                  </div>
                  <div className="text-muted-foreground text-xs">
                    {t.cheatsheet['Complete API documentation']}
                  </div>
                </div>
              </div>
              <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
            </a>

            <div className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-3">
              <div className="flex items-center gap-3">
                <div className="text-muted-foreground flex size-8 items-center justify-center">
                  <Webhook className="size-5" />
                </div>
                <div>
                  <div className="text-sm font-semibold">
                    {t.cheatsheet['Webhooks']}
                  </div>
                  <div className="text-muted-foreground text-xs">
                    {t.cheatsheet['Event-driven integrations']}
                  </div>
                </div>
              </div>
            </div>

            <div className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-3">
              <div className="flex items-center gap-3">
                <div className="text-muted-foreground flex size-8 items-center justify-center">
                  <Settings className="size-5" />
                </div>
                <div>
                  <div className="text-sm font-semibold">
                    {t.cheatsheet['SDK Support']}
                  </div>
                  <div className="text-muted-foreground text-xs">
                    {t.cheatsheet['TypeScript, Python, more']}
                  </div>
                </div>
              </div>
            </div>

            <div className="hover:bg-muted/50 flex items-center justify-between rounded-md px-3 py-3">
              <div className="flex items-center gap-3">
                <div className="text-muted-foreground flex size-8 items-center justify-center">
                  <Globe className="size-5" />
                </div>
                <div>
                  <div className="text-sm font-semibold">
                    {t.cheatsheet['White-label Embedding']}
                  </div>
                  <div className="text-muted-foreground text-xs">
                    {t.cheatsheet['Custom branding options']}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CUSTOMER CASE STUDIES */}
      <section className="container pt-5 pb-12">
        <div className="border-border bg-card rounded-lg border lg:col-span-2">
          <div className="border-b border-emerald-200 bg-emerald-50 px-5 py-3 dark:border-emerald-900 dark:bg-emerald-950/30">
            <h2 className="text-base text-emerald-900 dark:text-emerald-100">
              {t.cheatsheet['Customer Success Stories']}
            </h2>
            <p className="text-xs text-emerald-700 dark:text-emerald-300">
              {t.cheatsheet['Leading companies automating support with Open']}
            </p>
          </div>
          <div className="grid gap-0 sm:grid-cols-2 md:grid-cols-3">
            {/* Column 1 */}
            <div className="divide-border divide-y p-4 md:border-e">
              <Link
                href="/customer-stories/moneygram"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <div className="text-sm font-semibold">MoneyGram</div>
                    <div className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-bold text-green-700">
                      70%
                    </div>
                  </div>
                  <div className="text-muted-foreground text-xs">
                    {t.cheatsheet['55M customers, 200+ countries']}
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/customer-stories/mollie"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <div className="text-sm font-semibold">Mollie</div>
                    <div className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-bold text-green-700">
                      60%
                    </div>
                  </div>
                  <div className="text-muted-foreground text-xs">
                    {t.cheatsheet['€6.5B payment processor']}
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/customer-stories/trunkrs"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <div className="text-sm font-semibold">Trunkrs</div>
                    <div className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-bold text-green-700">
                      80%
                    </div>
                  </div>
                  <div className="text-muted-foreground text-xs">
                    {t.cheatsheet['61K+ Google reviews']}
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/customer-stories/qoyod"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <div className="text-sm font-semibold">Qoyod</div>
                    <div className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-bold text-green-700">
                      77%
                    </div>
                  </div>
                  <div className="text-muted-foreground text-xs">
                    {t.cheatsheet['25K businesses']}
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>
            </div>

            {/* Column 2 */}
            <div className="divide-border divide-y p-4 md:border-e">
              <Link
                href="/customer-stories/ticketswap"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <div className="text-sm font-semibold">TicketSwap</div>
                    <div className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-bold text-green-700">
                      67%
                    </div>
                  </div>
                  <div className="text-muted-foreground text-xs">
                    {t.cheatsheet['15M users, 42 countries']}
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/customer-stories/more"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <div className="text-sm font-semibold">More.com</div>
                    <div className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-bold text-green-700">
                      67%
                    </div>
                  </div>
                  <div className="text-muted-foreground text-xs">
                    {t.cheatsheet['34M tickets sold']}
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/customer-stories/cookinglife"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <div className="text-sm font-semibold">CookingLife</div>
                    <div className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-bold text-green-700">
                      70%
                    </div>
                  </div>
                  <div className="text-muted-foreground text-xs">
                    {t.cheatsheet['1M+ customers served']}
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>
            </div>

            {/* Column 3 */}
            <div className="divide-border divide-y p-4">
              <Link
                href="/customer-stories/oto"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <div className="text-sm font-semibold">OTO</div>
                    <div className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-bold text-green-700">
                      77%
                    </div>
                  </div>
                  <div className="text-muted-foreground text-xs">
                    {t.cheatsheet['Thousands of merchants']}
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/customer-stories/deonlinedrogist"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <div className="text-sm font-semibold">DeOnlineDrogist</div>
                    <div className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-bold text-green-700">
                      73%
                    </div>
                  </div>
                  <div className="text-muted-foreground text-xs">
                    {t.cheatsheet['Top online pharmacy NL']}
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/customer-stories/ocn"
                className="hover:bg-muted/50 group flex items-center justify-between rounded-md px-3 py-3 transition-colors"
              >
                <div className="flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <div className="text-sm font-semibold">One Car Now</div>
                    <div className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-bold text-green-700">
                      60%
                    </div>
                  </div>
                  <div className="text-muted-foreground text-xs">
                    {t.cheatsheet['$86M-funded car subscription']}
                  </div>
                </div>
                <ArrowRight className="text-muted-foreground size-4 opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>

              <Link
                href="/customer-stories"
                className="hover:bg-muted/50 group bg-primary/5 mt-4 flex items-center justify-between rounded-md border px-3 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="text-primary flex size-6 items-center justify-center">
                    <ArrowRight className="size-4 rtl:rotate-180" />
                  </div>
                  <div className="text-primary text-sm font-semibold">
                    {t.cheatsheet['View All Success Stories']}
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Print Footer - Only visible when printing */}
      <div className="hidden print:mt-6 print:block">
        <div className="border-t-2 border-neutral-300 pt-2">
          <div className="grid grid-cols-3 gap-4 text-xs">
            <div>
              <p className="font-semibold text-neutral-900">
                {t.footer['Get Started']}
              </p>
              <p className="mt-1 text-neutral-600">open.cx/demo</p>
            </div>
            <div className="text-center">
              <p className="font-semibold text-neutral-900">
                {isAr ? 'التوثيق' : 'Documentation'}
              </p>
              <p className="mt-1 text-neutral-600">docs.open.cx</p>
            </div>
            <div className="text-end">
              <p className="font-semibold text-neutral-900">
                {t.footer['Contact']}
              </p>
              <p className="mt-1 text-neutral-600">hello@open.cx</p>
            </div>
          </div>
          <p className="mt-2 text-center text-xs text-neutral-400">
            {
              t.cheatsheet[
                '© 2026 Open. Y Combinator W24 • $7M Raised • Serving 55M+ end users globally'
              ]
            }
          </p>
        </div>
      </div>
    </div>
  );
}
